import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';

const STEPS = [1, 2, 3, 4, 5];

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '14px 16px',
  color: '#ffffff',
  fontSize: 16,
  fontFamily: 'Inter, sans-serif',
  outline: 'none',
  marginTop: 8,
  boxSizing: 'border-box',
};

const BOTTLENECK_OPTIONS = [
  { label: 'Getting Leads', crew: 'Sales Crew', crewParam: 'sales' },
  { label: 'Closing Deals', crew: 'Sales Crew', crewParam: 'sales' },
  { label: 'Delivering Work', crew: 'Delivery Crew', crewParam: 'delivery' },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { session, refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [onboardingContext, setOnboardingContext] = useState({
    business_name: '',
    offer: '',
    icp: '',
    bottleneck: '',
  });
  const [selectedBottleneck, setSelectedBottleneck] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedOption = BOTTLENECK_OPTIONS?.find(o => o?.label === selectedBottleneck);
  const crewName = selectedOption?.crew || '';
  const crewParam = selectedOption?.crewParam || 'sales';

  const handleNext = () => {
    if (step < 5) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleBottleneckSelect = (label) => {
    setSelectedBottleneck(label);
    setOnboardingContext(prev => ({ ...prev, bottleneck: label }));
  };

  const handleLaunch = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = session?.access_token;
      if (!token) throw new Error('Authentication session is unavailable');

      const res = await fetch(apiUrl('/api/profile/onboarding'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ onboarding_context: onboardingContext }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refreshProfile();
    } catch (err) {
      console.error('Onboarding POST failed:', err);
      setError('Failed to save onboarding. Please try again.');
      setSubmitting(false);
      return;
    } finally {
      setSubmitting(false);
    }
    navigate(`/operations?crew=${crewParam}`);
  };

  const canProceedStep1 = onboardingContext?.business_name?.trim()?.length > 0;
  const canProceedStep2 = onboardingContext?.offer?.trim()?.length > 0;
  const canProceedStep3 = onboardingContext?.icp?.trim()?.length > 0;
  const canProceedStep4 = selectedBottleneck !== null;

  return (
    <div
      style={{ backgroundColor: '#05060f', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}
      className="flex flex-col items-center justify-center px-4"
    >
      {/* Progress Dots */}
      <div className="flex items-center gap-3 mb-12">
        {STEPS?.map(s => (
          <div
            key={s}
            style={{
              width: s === step ? 10 : 8,
              height: s === step ? 10 : 8,
              borderRadius: '50%',
              backgroundColor: s === step ? '#2DC896' : s < step ? '#2DC896' : '#1e2235',
              opacity: s < step ? 0.5 : 1,
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
      {/* Step Content */}
      <div className="w-full max-w-lg">
        {/* Step 1 */}
        {step === 1 && (
          <StepWrapper
            heading="What's your business called?"
            onNext={handleNext}
            canNext={canProceedStep1}
            showBack={false}
            onBack={handleBack}
          >
            <input
              type="text"
              value={onboardingContext?.business_name}
              onChange={e => setOnboardingContext(prev => ({ ...prev, business_name: e?.target?.value }))}
              onKeyDown={e => e?.key === 'Enter' && canProceedStep1 && handleNext()}
              placeholder="e.g. Acme Corp"
              autoFocus
              style={inputStyle}
            />
          </StepWrapper>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <StepWrapper
            heading="What are you selling?"
            onNext={handleNext}
            canNext={canProceedStep2}
            showBack={true}
            onBack={handleBack}
          >
            <input
              type="text"
              value={onboardingContext?.offer}
              onChange={e => setOnboardingContext(prev => ({ ...prev, offer: e?.target?.value }))}
              onKeyDown={e => e?.key === 'Enter' && canProceedStep2 && handleNext()}
              placeholder="e.g. B2B SaaS for logistics teams"
              autoFocus
              style={inputStyle}
            />
          </StepWrapper>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <StepWrapper
            heading="Who buys it?"
            onNext={handleNext}
            canNext={canProceedStep3}
            showBack={true}
            onBack={handleBack}
          >
            <input
              type="text"
              value={onboardingContext?.icp}
              onChange={e => setOnboardingContext(prev => ({ ...prev, icp: e?.target?.value }))}
              onKeyDown={e => e?.key === 'Enter' && canProceedStep3 && handleNext()}
              placeholder="e.g. Operations managers at mid-size companies"
              autoFocus
              style={inputStyle}
            />
          </StepWrapper>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <StepWrapper
            heading="What's your biggest bottleneck right now?"
            onNext={handleNext}
            canNext={canProceedStep4}
            showBack={true}
            onBack={handleBack}
          >
            <div className="flex flex-col gap-3 mt-2">
              {BOTTLENECK_OPTIONS?.map(opt => (
                <button
                  key={opt?.label}
                  onClick={() => handleBottleneckSelect(opt?.label)}
                  style={{
                    background: selectedBottleneck === opt?.label ? 'rgba(45,200,150,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selectedBottleneck === opt?.label ? '#2DC896' : 'rgba(255,255,255,0.1)'}`,
                    color: selectedBottleneck === opt?.label ? '#2DC896' : '#a0aec0',
                    borderRadius: 8,
                    padding: '14px 20px',
                    textAlign: 'left',
                    fontSize: 15,
                    fontFamily: 'Inter, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    letterSpacing: '0.02em',
                  }}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
          </StepWrapper>
        )}

        {/* Step 5 */}
        {step === 5 && (
          <div className="flex flex-col items-center text-center">
            <p style={{ color: '#2DC896', fontSize: 12, letterSpacing: '0.15em', marginBottom: 16, fontFamily: 'Inter, sans-serif' }}>
              STEP 5 OF 5
            </p>
            <h1 style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 36, fontWeight: 700, color: '#ffffff', marginBottom: 12, lineHeight: 1.2 }}>
              Your first operation is ready
            </h1>
            <p style={{ color: '#718096', fontSize: 15, marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>
              Based on your bottleneck, we've mapped you to:
            </p>
            <div
              style={{
                background: 'rgba(45,200,150,0.08)',
                border: '1px solid rgba(45,200,150,0.3)',
                borderRadius: 10,
                padding: '18px 32px',
                marginBottom: 40,
                marginTop: 8,
              }}
            >
              <span style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 26, fontWeight: 700, color: '#2DC896', letterSpacing: '0.05em' }}>
                {crewName}
              </span>
            </div>

            {/* Back button */}
            <button
              onClick={handleBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#4a5568',
                fontSize: 13,
                cursor: 'pointer',
                marginBottom: 16,
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '0.05em',
              }}
            >
              ← BACK
            </button>

            <button
              onClick={handleLaunch}
              disabled={submitting}
              style={{
                background: submitting ? 'rgba(45,200,150,0.5)' : '#2DC896',
                color: '#05060f',
                border: 'none',
                borderRadius: 8,
                padding: '14px 40px',
                fontSize: 15,
                fontWeight: 700,
                fontFamily: 'Rajdhani, sans-serif',
                letterSpacing: '0.1em',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                width: '100%',
                maxWidth: 320,
              }}
            >
              {submitting ? 'LAUNCHING...' : `LAUNCH ${crewName?.toUpperCase()}`}
            </button>

            {error && (
              <p style={{ color: '#fc8181', fontSize: 13, marginTop: 12, fontFamily: 'Inter, sans-serif' }}>{error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StepWrapper = ({ heading, children, onNext, canNext, showBack, onBack }) => {
  const stepNum = showBack ? null : 1;
  return (
    <div className="flex flex-col">
      <h1
        style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: 34,
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: 24,
          lineHeight: 1.2,
        }}
      >
        {heading}
      </h1>
      {children}
      <div className="flex items-center gap-4 mt-8">
        {showBack && (
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#4a5568',
              borderRadius: 8,
              padding: '12px 20px',
              fontSize: 13,
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.05em',
              cursor: 'pointer',
            }}
          >
            ← BACK
          </button>
        )}
        <button
          onClick={onNext}
          disabled={!canNext}
          style={{
            background: canNext ? '#2DC896' : 'rgba(45,200,150,0.2)',
            color: canNext ? '#05060f' : 'rgba(45,200,150,0.5)',
            border: 'none',
            borderRadius: 8,
            padding: '13px 32px',
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'Rajdhani, sans-serif',
            letterSpacing: '0.1em',
            cursor: canNext ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            flex: showBack ? 1 : undefined,
            width: showBack ? undefined : '100%',
          }}
        >
          CONTINUE →
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
